import { useEffect, useState } from 'react';
import { HEADER_GRID_CLASSNAME } from '../constants';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { Button } from '@components/ui/button';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { MAX_MULTI_DAYS } from '@forms/schedules/internal/constants';
import { InternalScheduleFormValues } from '@forms/schedules/internal/types';
import { getExtensionDates } from '@forms/schedules/internal/helpers';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { useSchedulerContext } from '@contexts/scheduler';
import { useAppContext } from '@contexts/app';
import { formatTime, shortDayDate } from '@utils/i18n';
import { cn } from '@utils/cn';
import { getDurationUnit } from '../multiDay/helpers';
import { InternalScheduleExtendDaysInputsProps } from './types';
import { getForemanName } from './helpers';

export const InternalScheduleExtendDaysInputs = ({
  disabled,
  weekendDefaults,
  dateControl,
  dateCaption,
}: InternalScheduleExtendDaysInputsProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'schedule.multiDay' });
  const { setValue, watch } = useFormContext<InternalScheduleFormValues>();
  const {
    setState: setSidebarState,
    relatedMultischedules,
    currentSchedule,
    crewMembers,
    equipmentMembers,
  } = useSchedulerSidebar();
  const { active } = useSchedulerContext();
  const { tenant } = useAppContext();
  const [addDays, skipSaturday, skipSunday] = watch(['addDays', 'skipSaturday', 'skipSunday']);
  const options = { skipSaturday, skipSunday };
  const [daysInput, setDaysInput] = useState(String(addDays));

  const seriesParams = {
    relatedMultischedules,
    currentScheduleId: currentSchedule?.id,
    startsAt: active.schedule.startsAt,
    zone: tenant.zone,
  };
  const extensionDates = getExtensionDates({ ...seriesParams, count: addDays, options });
  const extensionKey = extensionDates.map((el) => el.getTime()).join();

  const applyAddDays = (value: number) => {
    const next = Math.min(Math.max(value, 0), MAX_MULTI_DAYS);
    setValue('addDays', next);
    setDaysInput(String(next));
  };

  useEffect(() => {
    setValue('skipSaturday', weekendDefaults.skipSaturday);
    setValue('skipSunday', weekendDefaults.skipSunday);
  }, [weekendDefaults.skipSaturday, weekendDefaults.skipSunday, setValue]);

  useEffect(() => {
    setSidebarState?.((prev) => ({ ...prev, extensionDates }));
  }, [extensionKey, setSidebarState]);

  const unit = getDurationUnit(addDays, options);
  const { startsAt, endsAt } = active.schedule;
  const time =
    startsAt && endsAt
      ? [startsAt, endsAt]
          .map((el) => formatTime(toZonedTime(parseISO(el), tenant.zone)))
          .join(' - ')
      : '';
  const preview = [
    getForemanName(active.schedule.foreman) || t('noForeman'),
    t('crewCount', { count: crewMembers.length }),
    equipmentMembers.length > 0 && t('equipmentCount', { count: equipmentMembers.length }),
    time,
  ].filter(Boolean);

  return (
    <div className={HEADER_GRID_CLASSNAME}>
      {dateControl}
      <div className="flex items-center gap-2 text-sm text-neutral-700">
        <span>{t('add')}</span>
        <div className="flex h-8 items-center rounded border border-neutral-300 bg-white">
          <Button
            variant="transparent"
            size="xs"
            className="h-full w-7 px-0 text-sm"
            disabled={disabled || addDays <= 0}
            aria-label={t('decrease')}
            onClick={() => applyAddDays(addDays - 1)}>
            <SvgConsumer id="minus" />
          </Button>
          <input
            type="text"
            inputMode="numeric"
            aria-label={t('addDays')}
            className="h-full w-10 bg-transparent text-center text-sm font-semibold outline-none"
            value={daysInput}
            disabled={disabled}
            onChange={(e) => {
              const digits = e.target.value.replace(/[^0-9]/g, '');
              setDaysInput(digits);
              if (digits) applyAddDays(Number(digits));
            }}
            onBlur={() => setDaysInput(String(addDays))}
          />
          <Button
            variant="transparent"
            size="xs"
            className="h-full w-7 px-0 text-sm"
            disabled={disabled || addDays >= MAX_MULTI_DAYS}
            aria-label={t('increase')}
            onClick={() => applyAddDays(addDays + 1)}>
            <SvgConsumer id="plus" />
          </Button>
        </div>
        <span>{t(`unit.${unit}`)}</span>
        {extensionDates.length > 0 && (
          <>
            <span className="text-neutral-400">→</span>
            <span className="whitespace-nowrap font-semibold text-neutral-800">
              {[
                ...new Set([extensionDates[0], extensionDates.at(-1) as Date].map(shortDayDate)),
              ].join(' – ')}
            </span>
          </>
        )}
      </div>
      <div className="flex justify-center">{dateCaption}</div>
      <div className="flex min-h-6 flex-wrap items-center gap-2 text-xs text-neutral-600">
        {addDays > 0 && (
          <>
            <span>{t('workWeekends')}</span>
            {(['skipSaturday', 'skipSunday'] as const).map((field) => (
              <button
                key={field}
                type="button"
                aria-pressed={!options[field]}
                disabled={disabled}
                onClick={() => setValue(field, !options[field])}
                className={cn(
                  'rounded-full border px-2.5 py-0.5 font-semibold transition-colors',
                  options[field]
                    ? 'border-neutral-300 bg-white text-neutral-500 hover:border-neutral-400'
                    : 'border-primary-500 bg-primary-50 text-primary-700',
                )}>
                {t(field === 'skipSaturday' ? 'saturday' : 'sunday')}
              </button>
            ))}
          </>
        )}
      </div>
      {addDays > 0 && (
        <p className="col-start-2 text-xs text-neutral-600">
          <span className="text-neutral-500">{t('copyPreview')}</span>{' '}
          <span className="font-medium text-neutral-800">{preview.join(' · ')}</span>
        </p>
      )}
    </div>
  );
};
